import {
  cookieMaxAge,
  cookieName,
  deLocalizeUrl,
  getLocale,
  getStrategyForUrl,
} from '../paraglide/runtime';
import { isTranslated } from './localeHead';

/**
 * How the signed-in app learns the reader's language.
 *
 * Sign-up, sign-in, the welcome flow and the dashboard have one URL in every
 * language, so they read a cookie instead of a URL prefix (the route
 * strategies in scripts/i18nConfig.ts). The cookie is written here, on the
 * client, after every render: a translated public page records the language
 * it is shown in, so a reader who arrives on `/zh` signs up in Chinese, and
 * one who switched back to English signs up in English. An untranslated page
 * (`/about`) is English in every language and records nothing, because
 * reading it is not a choice of language.
 *
 * Public pages never read the cookie, and nothing redirects on it.
 *
 * The same pass keeps `<html lang>` on the language of the render: an app
 * page's HTML shell is prerendered in English and does not know the cookie.
 */

/** Whether a URL takes its locale from the cookie rather than from its path. */
const isAppUrl = (url: string): boolean => !getStrategyForUrl(url).includes('url');

/** Writes the language the app pages render in. */
const recordAppLocale = (locale: string): void => {
  document.cookie = `${cookieName}=${locale}; path=/; max-age=${cookieMaxAge}; samesite=lax`;
};

/** Runs after every client render (the router's `onRendered` event). */
export const syncDocumentLocale = (): void => {
  const { href } = window.location;
  const locale = getLocale();
  document.documentElement.lang = locale;
  if (!isAppUrl(href) && isTranslated(deLocalizeUrl(href).pathname)) recordAppLocale(locale);
};
